import {
  flip,
  FloatingPortal,
  offset,
  Placement,
  shift,
  useFloating,
  useHover,
  useInteractions,
} from "@floating-ui/react";
import React, { PropsWithChildren, useState } from "react";
import styled from "styled-components";
import { CreateTransient } from "../../shared/types";
import { Typography } from "../typography";

type TooltipProps = PropsWithChildren<{
  placement?: Placement;
  content: React.ReactNode;
  disabled?: boolean;
  className?: string;
  maxWidth?: number;
}>;

export const Tooltip = (props: TooltipProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { refs, floatingStyles, context } = useFloating({
    placement: props.placement ?? "bottom-start",
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [
      // changes the placement of the floating element to keep it in view vertically.
      flip(),
      // changes the placement of the floating element to keep it in view horizontally.
      shift(),
      offset({ mainAxis: 4 }),
    ],
  });

  const hover = useHover(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  return (
    <>
      <div
        ref={refs.setReference}
        className={props.className}
        {...getReferenceProps()}
      >
        {props.children}
      </div>
      {isOpen && !!props.content && (
        <FloatingPortal>
          <TooltipPanel
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            $disabled={props.disabled}
            $maxWidth={props.maxWidth}
          >
            <Typography variant="body-x-small" color="WHITE">
              {props.content}
            </Typography>
          </TooltipPanel>
        </FloatingPortal>
      )}
    </>
  );
};

const TooltipPanel = styled.div<
  CreateTransient<{ disabled?: boolean; maxWidth?: number }>
>`
  // we need this index to make sure tooltip is above dialogs until we fix them.
  z-index: 1301;
  padding: 6px 8px;
  background-color: ${({ theme }) => theme.COLORS.GRAY_DARK};
  border-radius: 2px;
  max-width: ${({ $maxWidth }) => ($maxWidth ? `${$maxWidth}px` : "250px")};
  width: max-content;
  white-space: pre-line;
  overflow-wrap: break-word;
  word-break: normal;
  display: ${({ $disabled }) => ($disabled ? "none" : "block")};
`;
